/**
 * Разбор текста алгоритма ОГЭ (задание 15) → Python с вызовами robot.*.
 *
 * Команды: вверх вниз влево вправо закрасить
 * Условия: сверху/снизу/слева/справа свободно, и/или/не, скобки
 * Конструкции: если … то … [иначе …] все; нц пока … кц
 * Игнор: алг нач кон; комментарии с |
 */

export class OgeRobotLangError extends Error {
  constructor(message, line = null) {
    super(line != null ? `Строка ${line}: ${message}` : message);
    this.name = 'OgeRobotLangError';
    this.line = line;
  }
}

const MULTI_WORD = [
  ['нц пока', 'WHILE'],
  ['сверху свободно', 'FREE_UP'],
  ['снизу свободно', 'FREE_DOWN'],
  ['слева свободно', 'FREE_LEFT'],
  ['справа свободно', 'FREE_RIGHT'],
  ['сверху стена', 'WALL_UP'],
  ['снизу стена', 'WALL_DOWN'],
  ['слева стена', 'WALL_LEFT'],
  ['справа стена', 'WALL_RIGHT'],
  ['клетка закрашена', 'PAINTED'],
];

const SINGLE = [
  ['закрасить', 'PAINT'],
  ['вверх', 'UP'],
  ['вниз', 'DOWN'],
  ['влево', 'LEFT'],
  ['вправо', 'RIGHT'],
  ['если', 'IF'],
  ['то', 'THEN'],
  ['иначе', 'ELSE'],
  ['все', 'END_IF'],
  ['кц', 'END_WHILE'],
  ['алг', 'ALG'],
  ['нач', 'BEGIN'],
  ['кон', 'END'],
  ['и', 'AND'],
  ['или', 'OR'],
  ['не', 'NOT'],
];

function normalizeSource(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .toLowerCase();
}

/**
 * @param {string} text
 * @returns {{ type: string, line: number, value?: string }[]}
 */
export function tokenizeOgeRobot(text) {
  const src = normalizeSource(text);
  const tokens = [];
  let i = 0;
  let line = 1;

  const peekWord = (word) => {
    if (!src.startsWith(word, i)) return false;
    const after = i + word.length;
    if (after < src.length) {
      const ch = src[after];
      if (/[a-zа-яё0-9_]/i.test(ch)) return false;
    }
    return true;
  };

  while (i < src.length) {
    const ch = src[i];
    if (ch === '\n') {
      line += 1;
      i += 1;
      continue;
    }
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    if (ch === '|') {
      while (i < src.length && src[i] !== '\n') i += 1;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'LPAREN', line });
      i += 1;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'RPAREN', line });
      i += 1;
      continue;
    }

    let matched = false;
    for (const [word, type] of MULTI_WORD) {
      if (peekWord(word)) {
        tokens.push({ type, line });
        i += word.length;
        matched = true;
        break;
      }
    }
    if (matched) continue;

    for (const [word, type] of SINGLE) {
      if (peekWord(word)) {
        tokens.push({ type, line });
        i += word.length;
        matched = true;
        break;
      }
    }
    if (matched) continue;

    const start = i;
    while (i < src.length && !/\s/.test(src[i]) && src[i] !== '(' && src[i] !== ')' && src[i] !== '|') {
      i += 1;
    }
    const junk = src.slice(start, i);
    throw new OgeRobotLangError(`неизвестное слово «${junk}»`, line);
  }

  tokens.push({ type: 'EOF', line });
  return tokens;
}

function condToPy(type) {
  switch (type) {
    case 'FREE_UP': return 'robot.сверху_свободно()';
    case 'FREE_DOWN': return 'robot.снизу_свободно()';
    case 'FREE_LEFT': return 'robot.слева_свободно()';
    case 'FREE_RIGHT': return 'robot.справа_свободно()';
    case 'WALL_UP': return '(not robot.сверху_свободно())';
    case 'WALL_DOWN': return '(not robot.снизу_свободно())';
    case 'WALL_LEFT': return '(not robot.слева_свободно())';
    case 'WALL_RIGHT': return '(not robot.справа_свободно())';
    case 'PAINTED': return 'robot.клетка_закрашена()';
    default: return null;
  }
}

function cmdToPy(type) {
  switch (type) {
    case 'UP': return 'robot.вверх()';
    case 'DOWN': return 'robot.вниз()';
    case 'LEFT': return 'robot.влево()';
    case 'RIGHT': return 'robot.вправо()';
    case 'PAINT': return 'robot.закрасить()';
    default: return null;
  }
}

/**
 * @param {string} text
 * @returns {string} Python-код
 */
export function ogeRobotToPython(text) {
  const tokens = tokenizeOgeRobot(text);
  let pos = 0;

  const peek = () => tokens[pos];
  const take = (expected) => {
    const t = tokens[pos];
    if (expected && t.type !== expected) {
      throw new OgeRobotLangError(
        `ожидалось «${expected}», а пришло «${t.type}»`,
        t.line,
      );
    }
    pos += 1;
    return t;
  };
  const match = (type) => {
    if (peek().type === type) {
      pos += 1;
      return true;
    }
    return false;
  };

  const parseCondition = () => {
    const parseOr = () => {
      let left = parseAnd();
      while (match('OR')) {
        const right = parseAnd();
        left = `(${left} or ${right})`;
      }
      return left;
    };
    const parseAnd = () => {
      let left = parseUnary();
      while (match('AND')) {
        const right = parseUnary();
        left = `(${left} and ${right})`;
      }
      return left;
    };
    const parseUnary = () => {
      if (match('NOT')) {
        return `(not ${parseUnary()})`;
      }
      if (match('LPAREN')) {
        const inner = parseOr();
        if (!match('RPAREN')) {
          throw new OgeRobotLangError('ожидалась закрывающая скобка', peek().line);
        }
        return `(${inner})`;
      }
      const t = peek();
      const py = condToPy(t.type);
      if (!py) {
        throw new OgeRobotLangError(
          'в условии нужно «свободно», «стена» или «клетка закрашена»',
          t.line,
        );
      }
      take();
      return py;
    };
    return parseOr();
  };

  const indent = (n, line) => `${'    '.repeat(n)}${line}`;

  const parseBlock = (level, stopTypes) => {
    const lines = [];
    while (true) {
      const t = peek();
      if (t.type === 'EOF' || stopTypes.includes(t.type)) break;
      if (t.type === 'ALG' || t.type === 'BEGIN' || t.type === 'END') {
        take();
        continue;
      }
      if (t.type === 'IF') {
        take();
        const cond = parseCondition();
        if (!match('THEN')) {
          throw new OgeRobotLangError('после условия нужно «то»', peek().line);
        }
        lines.push(indent(level, `if ${cond}:`));
        const body = parseBlock(level + 1, ['END_IF', 'ELSE', 'EOF', ...stopTypes]);
        if (!body.length) {
          lines.push(indent(level + 1, 'pass'));
        } else {
          lines.push(...body);
        }
        if (match('ELSE')) {
          lines.push(indent(level, 'else:'));
          const alt = parseBlock(level + 1, ['END_IF', 'EOF', ...stopTypes]);
          if (!alt.length) {
            lines.push(indent(level + 1, 'pass'));
          } else {
            lines.push(...alt);
          }
        }
        if (!match('END_IF')) {
          throw new OgeRobotLangError('блок «если» должен заканчиваться словом «все»', peek().line);
        }
        continue;
      }
      if (t.type === 'WHILE') {
        take();
        const cond = parseCondition();
        lines.push(indent(level, `while ${cond}:`));
        const body = parseBlock(level + 1, ['END_WHILE', 'EOF', ...stopTypes]);
        if (!body.length) {
          lines.push(indent(level + 1, 'pass'));
        } else {
          lines.push(...body);
        }
        if (!match('END_WHILE')) {
          throw new OgeRobotLangError('цикл должен заканчиваться словом «кц»', peek().line);
        }
        continue;
      }
      const cmd = cmdToPy(t.type);
      if (cmd) {
        take();
        lines.push(indent(level, cmd));
        continue;
      }
      throw new OgeRobotLangError(`здесь не ожидается «${t.type}»`, t.line);
    }
    return lines;
  };

  const body = parseBlock(0, ['EOF']);
  if (peek().type !== 'EOF') {
    throw new OgeRobotLangError(`лишнее «${peek().type}»`, peek().line);
  }
  if (!body.length) {
    throw new OgeRobotLangError('пустой алгоритм');
  }
  return `import robot\n${body.join('\n')}\n`;
}

/**
 * Готовит код к запуску: при input_lang=oge разбирает текст экзамена.
 * @param {string} code
 * @param {object|null} canvasConfig
 * @returns {string}
 */
export function prepareRobotSource(code, canvasConfig) {
  const lang = String(canvasConfig?.input_lang || canvasConfig?.lang || '').toLowerCase();
  if (lang === 'oge' || lang === 'oge_robot' || lang === 'kumir') {
    return ogeRobotToPython(code);
  }
  return code || '';
}
